import { computed, onMounted } from 'vue';
import { SimpleStorage } from 'assets/js/classes/Storage.js';
import { KEY_CART_PRODUCTS } from 'assets/js/consts/storageKeys.const.js';
import { useAppStore } from '~/store/StoreApp.js';
import make_query_params from 'assets/js/functions/route/make_query_params.js';
import copy_clipboard from 'assets/js/functions/copy_clipboard.js';
import useToast from 'assets/js/composables/layout/useToast.js';
import qs from 'qs';
import useListFetch from 'assets/js/composables/fetcher/useListFetch.js';
import { indexProductVariationsPerWarehouse } from 'assets/js/api/productVariation.api.js';
import { useRoute } from 'vue-router';
import useShare from '~/assets/js/composables/fetcher/useShare';
import isString from 'lodash/isString';

const useCart = (storeSlug = null) => {
  const route = useRoute();
  const appStore = useAppStore();

  if (!storeSlug) {
    storeSlug = route.params?.store_slug;
  }
  const storageKey = KEY_CART_PRODUCTS + (storeSlug ? `-${storeSlug}` : '');

  const cart = computed(() => {
    return appStore.cart;
  });

  const cart_total_quantity = computed(() => {
    return appStore.cart.reduce((acc, p) => acc + p.quantity, 0);
  });

  const variations_count = computed(() => {
    return cart.value.reduce((acc, p) => acc + p.quantity, 0);
  });

  const shipping_price = computed(() => {
    return cartWarehouseVariationGroups.value.reduce(
      (acc, group) => acc + group.warehouse.shipping.$price,
      0
    );
  });

  const products_price = computed(() => {
    return cart_product_variations.value.reduce(
      (acc, pv) =>
        acc +
        cart.value.find(cp => cp.id === pv.id).quantity *
          pv.product.quantity_in_package *
          pv.$price,
      0
    );
  });

  const total_price = computed(() => {
    return products_price.value + shipping_price.value;
  });

  const {
    data: data_product_variations_per_warehouse,
    has_error: has_error_product_variations_per_warehouse,
    is_loading: is_loading_product_variations_per_warehouse,
    filters: filters_product_variations_per_warehouse,
    isLoadmoreDone: isLoadmoreDone_product_variations_per_warehouse,
    fetch: fetch_product_variations_per_warehouse,
    filter: filter_product_variations_per_warehouse,
    loadMore: loadMore_product_variations_per_warehouse,
    paginate: paginate_product_variations_per_warehouse,
    change_per_page: change_per_page_product_variations_per_warehouse,
  } = useListFetch(indexProductVariationsPerWarehouse, {
    manual: true,
    in_load: true,
    onFetch(rsData) {
      const remotePvCount = rsData.list.reduce(
        (acc, item) => acc + item.product_variations.length,
        0
      );

      // TODO: make a sync function, to sync quantity if the selected value is greater than actual quantity
      // TODO: make remove uncorrect pv from cart
      if (remotePvCount !== cart.value.length) {
        clearCart();
      }

      return rsData;
    },
  });

  const cartWarehouseVariationGroups = computed(() => {
    const cartVariationIds = new Set(cart.value.map(item => item.id));

    const groupsWithFilteredVariations = data_product_variations_per_warehouse.value.list.map(
      group => {
        const variationsInCart = group.product_variations.filter(variation =>
          cartVariationIds.has(variation.id)
        );

        return {
          ...group,
          product_variations: variationsInCart,
        };
      }
    );

    return groupsWithFilteredVariations.filter(group => group.product_variations.length > 0);
  });

  const cart_product_variations = computed(() => {
    return cartWarehouseVariationGroups.value.reduce((acc, item) => {
      acc.push(...item.product_variations);
      return acc;
    }, []);
  });

  const is_cart_ok = cart => {
    return cart.every(p => !isNaN(p.id) && !isNaN(p.quantity));
  };

  const save_cart = () => {
    SimpleStorage.setItem(storageKey, appStore.cart);
  };

  const retrieveCart = () => {
    return SimpleStorage.getItem(storageKey) ?? [];
  };

  const addToCart = product_variation_id => {
    const found_product_index = appStore.cart.findIndex(p => p.id === product_variation_id);

    if (found_product_index !== -1) {
      appStore.cart[found_product_index].quantity++;
    } else {
      appStore.cart.push({
        id: product_variation_id,
        quantity: 1,
      });
    }

    save_cart();
  };

  const minusFromCart = product_variation_id => {
    const found_product_index = appStore.cart.findIndex(p => p.id === product_variation_id);

    if (found_product_index === -1) return;

    appStore.cart[found_product_index].quantity--;
    if (appStore.cart[found_product_index].quantity <= 0) {
      appStore.cart = appStore.cart.filter(p => p.id !== product_variation_id);
    }

    save_cart();
  };

  const setToCart = (product_variation_id, quantity) => {
    const found_product_index = appStore.cart.findIndex(p => p.id === product_variation_id);

    if (found_product_index !== -1) {
      appStore.cart[found_product_index].quantity = quantity;

      if (appStore.cart[found_product_index].quantity <= 0) {
        appStore.cart = appStore.cart.filter(p => p.id !== product_variation_id);
      }
    } else {
      appStore.cart.push({
        id: product_variation_id,
        quantity,
      });
    }

    save_cart();
  };

  const clearCart = () => {
    appStore.cart = [];

    save_cart();
  };

  const deleteCart = () => {
    SimpleStorage.removeItem(storageKey);
  };

  const shareCart = () => {
    const utm_medium = isString(storeSlug) ? `warehouse-cart` : 'store-cart';

    const query_string = make_query_params({
      cart: cart.value,
      utm_source: 'share',
      utm_medium,
      ...(isString(storeSlug) && { utm_campaign: 'cart-share-' + storeSlug }),
    });

    const shareLink = window.location.origin + window.location.pathname + query_string;

    useShare().shareLink(
      shareLink,
      'اشتراک گذاری سبد خرید',
      'سبد خرید خود را با دیگران به اشتراک بگذارید'
    );
  };

  const loadCart = cart_items => {
    for (const item of cart_items) {
      setToCart(+item.id, +item.quantity);
    }
  };

  onMounted(() => {
    if (appStore.is_cart_loaded) return;

    try {
      const query_params = qs.parse(window.location.search);

      if (Array.isArray(query_params?.cart) && is_cart_ok(query_params?.cart)) {
        appStore.cart = query_params.cart.map(p => ({
          id: +p.id,
          quantity: +p.quantity,
        }));

        save_cart();
      } else {
        const cart = retrieveCart();

        if (is_cart_ok(cart)) {
          appStore.cart = cart;
        }
      }
    } catch (err) {
      console.log(err);

      save_cart();

      clearCart();
    }

    appStore.is_cart_loaded = true;
  });

  return {
    cart,
    variations_count,
    cart_total_quantity,
    products_price,
    shipping_price,
    total_price,
    data_product_variations_per_warehouse,
    cart_product_variations,
    is_loading_product_variations_per_warehouse,
    cartWarehouseVariationGroups,
    fetch_product_variations_per_warehouse,
    addToCart,
    minusFromCart,
    setToCart,
    clearCart,
    deleteCart,
    shareCart,
    loadCart,
  };
};

export default useCart;
